/**
 * useSecondTabDetection — AC4.
 *
 * Uses BroadcastChannel('bimassess-attempt') to detect when a second browser tab
 * opens the same attempt. Implements first-claim-wins semantics:
 *
 * - On mount: posts { type: 'claim', attemptId, claimedAt: Date.now() }
 * - On receiving a claim message for the same attemptId:
 *   - If our own claimedAt > received claimedAt → WE claimed later → we are the
 *     secondary tab → setIsSecondTab(true)
 *   - If our own claimedAt <= received claimedAt → WE claimed earlier → stay primary
 * - On unmount: closes the channel
 *
 * Race resolution: the tab with the LOWER claimedAt timestamp wins (was first).
 * Two tabs claiming within the same millisecond: the one whose message arrives
 * second loses (its claimedAt will equal the winner's, so <= check keeps winner
 * as primary). In the equal case we fall through to no-op (first receiver wins).
 */
import { useEffect, useRef, useState } from 'react';

interface SecondTabState {
  isSecondTab: boolean;
}

interface ClaimMessage {
  type: 'claim';
  attemptId: string;
  claimedAt: number;
}

export function useSecondTabDetection(attemptId: string): SecondTabState {
  const [isSecondTab, setIsSecondTab] = useState(false);
  // Capture our own claimedAt at mount time (stable ref, does not change)
  const claimedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const channel = new BroadcastChannel('bimassess-attempt');
    const ownClaimedAt = claimedAtRef.current;

    // Claim this attempt — include the timestamp for asymmetric resolution
    channel.postMessage({ type: 'claim', attemptId, claimedAt: ownClaimedAt } as ClaimMessage);

    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as ClaimMessage;
      if (data.type !== 'claim' || data.attemptId !== attemptId) return;

      // First-claim-wins: if our claim timestamp is GREATER than the received one,
      // the other tab claimed before us → we are the secondary tab.
      if (ownClaimedAt > data.claimedAt) {
        setIsSecondTab(true);
      }
      // Otherwise (ownClaimedAt <= received): we claimed earlier or at the same
      // moment — stay primary (no-op).
    };

    return () => {
      channel.close();
    };
  }, [attemptId]);

  return { isSecondTab };
}
